import { useSetState, useRequest } from 'ahooks'
import { Toast } from 'antd-mobile'
import { get as objGet, omit, pick, cloneDeep } from 'lodash-es'
import { observer } from 'mobx-react'
import React, { useEffect, useMemo } from 'react'

import { HCell, HPopup, HSearchView, HTreeNode } from '@/components/NewEditForm/UILibrary/HYBVant'
import { publicEvents } from '@/components/NewEditForm/UILibrary/HYBVant/utils'
import { SELECT_MODE } from '@/constants'
import { queryDataList } from '@/service'
import { isNotEmptyArray, dealCellTitle, findValuesByKeys } from '@/utils'

import CONFIG_MAP from './config'
import { dealDataList, dealQueryConfig, dealCustomItems } from './utils'

interface BigDataSelectProps {
  value?: any
  onConfirm?: (value: any) => void
  category?: keyof typeof CONFIG_MAP
  required?: boolean
  onInitData?: (v: any) => void
  initArgument?: {
    paged?: boolean
    enable_status?: number
    [key: string]: any
  }
  fieldNames?: {
    label_name?: string
    label_text?: string
    value_name?: string
  }
  disabled?: boolean
  mode?: 'single' | 'multiple' | string
  children?: React.ReactNode
  config?: any
  [key: string]: any
}

// 字段配置
const FIELD_CONFIG = `
{
  "label": "银行名称",
  "name": "bank_name",
  "placeholder": "请选择",
  "type": "BigDataSelect",
  "category": "BankName"
}
`

const isStringOrNumber = (value: any) => {
  return ['number', 'string'].includes(typeof value)
}

const DEFAULT_FIELD_NAMES = { label: 'label', value: 'value' }

/**
 * 大数组选择
 * @description 根据category获取配置，category为空时，使用restProps
 * @param value
 * @param onConfirm
 * @param category 数据类型
 * @param fieldNames
 * @param required
 * @param disabled
 * @param mode
 * @returns
 */
const BigDataSelect: React.FC<BigDataSelectProps> = (props) => {
  const { onConfirm, onInitData, onAfterChange, ...restProps } = props || {}
  const [
    { allCount, dataList, originValueList, keyword, selectedList, originalDataList, visible },
    setState
  ] = useSetState<any>({
    allCount: 0,
    keyword: '',
    originalDataList: [],
    originValueList: [],
    selectedList: [],
    dataList: [],
    visible: false
  })

  //  默认单选
  const mode = findValuesByKeys(props, ['mode'])?.mode || SELECT_MODE.SINGLE

  // 是否是单选
  const isSingle = mode === 'single'

  // 传进来的数据
  const propsDatalist = useMemo(() => {
    return restProps?.dataList || restProps?.options
  }, [restProps?.dataList, restProps?.options])

  const {
    url = '',
    method = 'post',
    dataPath,
    fieldNames,
    enableCustomItems = false,
    searchParams
  } = dealQueryConfig(props)

  // 查询数据列表
  const { run: runQueryDataList } = useRequest(() => queryDataList(searchParams, method, url), {
    manual: true,
    onSuccess: ({ data }: any) => {
      const tempData = dataPath ? objGet(data, dataPath) : data
      const tempDataList = dealDataList(tempData, fieldNames)
      onInitData?.(tempData)
      setState({
        dataList: tempDataList,
        originalDataList: tempDataList,
        allCount: tempDataList?.length || 0
      })
    }
  })

  // 搜索关键字
  const onSearchChange = (keyword: string) => {
    let filterList = cloneDeep(originalDataList)

    if (keyword) {
      filterList = filterList?.filter((x: any) => x?.label?.includes(keyword))
      // 如果允许自定义项
      if (enableCustomItems && mode === 'single') {
        filterList = dealCustomItems(filterList, keyword)
      }
    }
    setState({
      keyword,
      dataList: filterList
    })
  }

  // 确认
  const handleConfirm = (list: any[]) => {
    if (restProps?.required && !list?.length) {
      Toast.show({
        content: '请至少选择一项'
      })
      return
    }

    const tempValue = isSingle ? list?.[0]?.value : list?.map((x: any) => x?.value)
    setState({ visible: false })
    publicEvents.onChange.call({ ...props, extra: list }, tempValue)
    onConfirm?.(tempValue)
    onAfterChange?.(list)
  }

  // 处理 value
  const finalValue: any = useMemo(() => {
    const tempValue = restProps?.value
    if (Array.isArray(tempValue)) {
      return tempValue?.map((x: any) => (isStringOrNumber(x) ? { value: x } : x))
    }
    if (tempValue && isStringOrNumber(tempValue)) {
      return [{ value: tempValue }]
    }
    return tempValue ? [tempValue] : []
  }, [restProps?.value, originalDataList, visible])

  // 如果禁用自定义项目, 如 银行名称
  const finalList = useMemo(() => {
    if (!(enableCustomItems && mode === 'single' && restProps?.value)) {
      return dataList
    }
    return dealCustomItems(dataList, restProps?.value as string)
  }, [dataList, enableCustomItems, restProps?.value])

  const onClose = () => {
    setState({ visible: false })
  }

  // 选择
  const onListChange = (val: any, list?: any[]) => {
    setState({ selectedList: list })
  }

  // 移除
  const onRemove = (item: any, list: any[]) => {
    setState({ selectedList: list })
  }

  // 全选
  const handleAllSelectedChange = (flag: boolean) => {
    setState({ selectedList: flag ? cloneDeep(originalDataList) : [] })
  }

  const cellProps = pick(restProps, ['disabled', 'placeholder', 'arrowIcon', 'readed'])
  // 是否可点击
  const bool = !restProps?.disabled && !restProps?.readed

  const cellTitle = dealCellTitle({
    value: originValueList,
    fieldNames: DEFAULT_FIELD_NAMES,
    suffix: restProps?.label || ''
  })

  // 是否全选
  const allSelected = useMemo(() => {
    return !!selectedList?.length && selectedList?.length === originalDataList?.length
  }, [selectedList, originalDataList])

  useEffect(() => {
    if (url) {
      runQueryDataList()
    } else if (!url && isNotEmptyArray(propsDatalist)) {
      setState({ dataList: propsDatalist || [] })
    }
  }, [url, propsDatalist, runQueryDataList])

  // 处理 value
  useEffect(() => {
    if (isNotEmptyArray(originalDataList) && Array.isArray(finalValue)) {
      const temVal = finalValue.map((v: any) => {
        const tempList = dealCustomItems(cloneDeep(originalDataList), v.value as string)
        const item = tempList.find((item: any) => v.value === item.value)
        return item
      })
      setState({
        originValueList: temVal,
        selectedList: temVal
      })
    }
  }, [originalDataList, finalValue])

  useEffect(() => {
    if (!visible) {
      setState({ keyword: '' })
      onSearchChange('')
    }
  }, [visible])

  return (
    <>
      {restProps?.children ? (
        <div
          className='h-tree-select-cell-wrap'
          onClick={() => !restProps?.disabled && setState({ visible: true })}
        >
          {restProps?.children}
        </div>
      ) : (
        <HCell
          value={cellTitle}
          onClick={() => bool && setState({ visible: true })}
          {...cellProps}
        />
      )}
      <HPopup
        className='h-search-select-popup'
        title={`选择${restProps?.label || ''}`}
        closeOnMaskClick
        visible={visible}
        size='lg'
        onMaskClick={onClose}
        onCancel={onClose}
        hasOk={false}
      >
        <HSearchView
          mode={mode}
          fieldNames={DEFAULT_FIELD_NAMES}
          count={allCount}
          allSelected={allSelected}
          selectedList={selectedList}
          onRemove={onRemove}
          keyword={keyword}
          onKeywordChange={onSearchChange}
          onAllSelectedChange={handleAllSelectedChange}
          onConfirm={handleConfirm}
        >
          <HTreeNode
            data={finalList}
            mode={mode}
            value={selectedList}
            keyword={keyword}
            fieldNames={DEFAULT_FIELD_NAMES}
            onChange={onListChange}
          />
        </HSearchView>
      </HPopup>
    </>
  )
}

export default observer(BigDataSelect)
